import { useState, useEffect } from 'react';
import { announcements } from '../../../mocks/announcements';

interface AnnouncementModalProps {
  onClose: () => void;
}

export default function AnnouncementModal({ onClose }: AnnouncementModalProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // 篩選重要公告（優先顯示 promotion / sale / notice）
  const importantAnnouncements = announcements
    .filter(ann => ['promotion', 'sale', 'notice'].includes(ann.type))
    .slice(0, 2);

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem('hideAnnouncementModal', 'true');
    }
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const getTypeConfig = (type: string) => {
    switch (type) {
      case 'promotion':
        return {
          label: '活動',
          bgColor: 'bg-gradient-to-r from-purple-500 to-pink-500',
          textColor: 'text-purple-600',
          borderColor: 'border-purple-200',
          bgLight: 'bg-purple-50'
        };
      case 'sale':
        return {
          label: '特賣',
          bgColor: 'bg-gradient-to-r from-red-500 to-orange-500',
          textColor: 'text-red-600',
          borderColor: 'border-red-200',
          bgLight: 'bg-red-50'
        };
      case 'notice':
        return {
          label: '通知',
          bgColor: 'bg-gradient-to-r from-blue-500 to-cyan-500',
          textColor: 'text-blue-600',
          borderColor: 'border-blue-200',
          bgLight: 'bg-blue-50'
        };
      default:
        return {
          label: '公告',
          bgColor: 'bg-gradient-to-r from-gray-500 to-gray-600',
          textColor: 'text-gray-600',
          borderColor: 'border-gray-200',
          bgLight: 'bg-gray-50'
        };
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden animate-[fadeIn_0.3s_ease-out]">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-400 to-orange-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg backdrop-blur-sm">
              <i className="ri-notification-3-line text-xl text-white"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">最新公告</h3>
              <p className="text-xs text-white/90">重要活動資訊</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors"
            aria-label="關閉"
          >
            <i className="ri-close-line text-xl text-white"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 max-h-[calc(90vh-180px)] overflow-y-auto">
          {importantAnnouncements.map((announcement) => {
            const typeConfig = getTypeConfig(announcement.type);
            return (
              <div
                key={announcement.id}
                className={`border-2 ${typeConfig.borderColor} rounded-xl overflow-hidden hover:shadow-lg transition-shadow`}
              >
                {/* 類型標籤 */}
                <div className={`${typeConfig.bgColor} px-4 py-2`}>
                  <span className="text-sm font-semibold text-white">
                    {typeConfig.label}
                  </span>
                </div>

                {/* 內容 */}
                <div className={`${typeConfig.bgLight} p-4`}>
                  <p className={`text-sm ${typeConfig.textColor} leading-relaxed mb-3`}>
                    {announcement.text}
                  </p>
                  <a
                    href={announcement.link}
                    className={`inline-flex items-center gap-2 px-4 py-2 ${typeConfig.bgColor} text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleClose();
                      window.REACT_APP_NAVIGATE(announcement.link);
                    }}
                  >
                    <span>查看詳情</span>
                    <i className="ri-arrow-right-line text-base"></i>
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500 cursor-pointer"
            />
            <span className="text-sm text-gray-600">不再顯示此公告</span>
          </label>
        </div>
      </div>
    </div>
  );
}

// 添加動畫樣式
const style = document.createElement('style');
style.textContent = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;
document.head.appendChild(style);